/**
 * REAL person segmentation — MediaPipe `ImageSegmenter` with the selfie model.
 *
 * This replaces the centred-ellipse placeholder that shipped in wave 3: an
 * ellipse cuts off shoulders, leaks the room around a moving head, and looks
 * exactly as fake as it is. The model is 244 kB, the WASM runtime ~11 MB, and
 * BOTH are fetched lazily on the first frame that needs an effect — a meeting
 * joined without background effects downloads none of it.
 *
 * The whole module is fail-soft. Anything that goes wrong returns null and the
 * caller publishes the untouched camera track.
 */
import type { ImageSegmenter } from '@mediapipe/tasks-vision';

/** Served by `apps/web/vite/mediapipeAssets.ts` from node_modules. */
const WASM_BASE = '/mediapipe';
const MODEL_URL = '/models/selfie_segmenter.tflite';

/** Below this many logical cores, segmentation will not hold a usable frame rate. */
const MIN_CORES = 4;

export interface PersonSegmenter {
  readonly width: number;
  readonly height: number;
  /** Person confidence, 0..1 per pixel, row-major. Null if the frame was rejected. */
  segment(frame: HTMLVideoElement, timestampMs: number): Float32Array | null;
  close(): void;
}

/** Why segmentation is unavailable, in terms the settings UI can explain. */
export type SegmenterBlock = 'unsupported' | 'save-data' | 'load-failed';

interface ConnectionHints {
  readonly saveData: boolean;
  readonly effectiveType: string;
}

/** `NetworkInformation` is not in lib.dom; read it defensively, never assert it. */
function connectionHints(): ConnectionHints {
  const value: unknown = Reflect.get(navigator, 'connection');
  if (typeof value !== 'object' || value === null) return { saveData: false, effectiveType: '' };
  const saveData: unknown = Reflect.get(value, 'saveData');
  const effectiveType: unknown = Reflect.get(value, 'effectiveType');
  return {
    saveData: saveData === true,
    effectiveType: typeof effectiveType === 'string' ? effectiveType : '',
  };
}

/**
 * The cheap pre-flight, run BEFORE anything is downloaded. Refusing an 11 MB
 * runtime on a Data-Saver phone is the whole point of the density work — a
 * background effect must never be the reason someone's call is unusable.
 */
export function segmentationBlock(): SegmenterBlock | null {
  if (typeof WebAssembly !== 'object' || typeof OffscreenCanvas === 'undefined') return 'unsupported';
  if ((navigator.hardwareConcurrency || 0) < MIN_CORES) return 'unsupported';
  const hints = connectionHints();
  if (hints.saveData || hints.effectiveType === 'slow-2g' || hints.effectiveType === '2g') {
    return 'save-data';
  }
  return null;
}

/** The model may emit one foreground channel or a background/person pair. */
function personMaskIndex(labels: readonly string[]): number {
  const found = labels.findIndex((label) => label.toLowerCase() !== 'background');
  return found >= 0 ? found : Math.max(0, labels.length - 1);
}

let instance: Promise<ImageSegmenter | null> | null = null;

async function createSegmenter(): Promise<ImageSegmenter | null> {
  try {
    const vision = await import('@mediapipe/tasks-vision');
    const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE);
    return await vision.ImageSegmenter.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });
  } catch {
    instance = null;
    return null;
  }
}

/**
 * Loads (once per tab) and wraps the segmenter. The returned `segment` copies
 * the mask out of MediaPipe's arena before the result is closed — the array it
 * hands back is only valid inside the callback.
 */
export async function loadPersonSegmenter(): Promise<PersonSegmenter | null> {
  if (segmentationBlock() !== null) return null;
  instance ??= createSegmenter();
  const segmenter = await instance;
  if (!segmenter) return null;

  const index = personMaskIndex(segmenter.getLabels());
  let buffer = new Float32Array(0);
  let width = 0;
  let height = 0;

  return {
    get width() {
      return width;
    },
    get height() {
      return height;
    },

    segment(frame, timestampMs) {
      let result;
      try {
        result = segmenter.segmentForVideo(frame, timestampMs);
      } catch {
        return null;
      }
      const mask = result.confidenceMasks?.[index] ?? result.confidenceMasks?.[0];
      if (!mask) {
        result.close();
        return null;
      }
      const data = mask.getAsFloat32Array();
      if (buffer.length !== data.length) buffer = new Float32Array(data.length);
      buffer.set(data);
      width = mask.width;
      height = mask.height;
      result.close();
      return buffer;
    },

    close() {
      /* The ImageSegmenter is shared for the tab's lifetime; closing it here
       * would force an 11 MB re-init the next time blur is toggled on. */
    },
  };
}
